/**
 * A2FEmotionRenderer.js
 *
 * Drives an avatar's facial expression from NVIDIA Audio2Face's per-utterance
 * 10-emotion vector (joy, sadness, anger, fear, amazement, grief, disgust,
 * cheekiness, pain, outOfBreath — each 0..1).
 *
 * Each A2F emotion maps to a curated set of ARKit 52 blendshapes
 * (`A2F_TO_ARKIT_MAP`). All active emotions blend simultaneously: the per-shape
 * contribution is `weight_in_map * emotion_strength`, summed across emotions
 * and capped at `1.0` so a multi-emotion stack never over-saturates the face.
 *
 * The renderer deliberately does NOT touch mouth / lip / jaw / cheek shapes —
 * those are owned by the lip-sync driver during speech, and including them
 * here would cause a per-frame fight on the same morphTargetInfluences entry.
 * Emotion is therefore expressed via brow / eye / nose only.
 *
 * Decay-to-neutral:
 *   When no new emotion vector has been received for `decayHoldMs`, the
 *   target vector linearly fades to zero over the next `decayDurationMs`.
 *   Total settle time after the last update is `decayHoldMs + decayDurationMs`.
 *   Defaults give ~2 s back-to-neutral, matching the spec.
 *
 * Tick wiring:
 *   - During speech: the lip-sync driver fires `onAfterTick` on every rAF tick
 *     after it has written its lip-sync values; the renderer then writes
 *     emotion shapes on top so emotion always wins on shared shapes.
 *   - During silence: the driver's tick is dormant. An independent rAF loop
 *     (`_idleTick`) runs the same renderer logic so decay is animated.
 */

export const A2F_EMOTION_NAMES = [
  "amazement",
  "anger",
  "cheekiness",
  "disgust",
  "fear",
  "grief",
  "joy",
  "outOfBreath",
  "pain",
  "sadness",
];

export const A2F_TO_ARKIT_MAP = {
  joy: {
    eyeSquintLeft: 0.5,
    eyeSquintRight: 0.5,
    browInnerUp: 0.15,
  },
  amazement: {
    eyeWideLeft: 0.7,
    eyeWideRight: 0.7,
    browInnerUp: 0.6,
    browOuterUpLeft: 0.5,
    browOuterUpRight: 0.5,
  },
  anger: {
    browDownLeft: 0.7,
    browDownRight: 0.7,
    noseSneerLeft: 0.4,
    noseSneerRight: 0.4,
    eyeSquintLeft: 0.3,
    eyeSquintRight: 0.3,
  },
  cheekiness: {
    eyeSquintLeft: 0.3,
    eyeSquintRight: 0.55,
    browOuterUpLeft: 0.25,
    browInnerUp: 0.1,
  },
  disgust: {
    noseSneerLeft: 0.7,
    noseSneerRight: 0.7,
    browDownLeft: 0.5,
    browDownRight: 0.5,
    eyeSquintLeft: 0.4,
    eyeSquintRight: 0.4,
  },
  fear: {
    eyeWideLeft: 0.7,
    eyeWideRight: 0.7,
    browInnerUp: 0.6,
    browOuterUpLeft: 0.4,
    browOuterUpRight: 0.4,
  },
  grief: {
    browInnerUp: 0.7,
    eyeSquintLeft: 0.4,
    eyeSquintRight: 0.4,
    browDownLeft: 0.15,
    browDownRight: 0.15,
  },
  outOfBreath: {
    browInnerUp: 0.2,
    eyeWideLeft: 0.2,
    eyeWideRight: 0.2,
  },
  pain: {
    browDownLeft: 0.4,
    browDownRight: 0.4,
    browInnerUp: 0.5,
    eyeSquintLeft: 0.7,
    eyeSquintRight: 0.7,
    noseSneerLeft: 0.3,
    noseSneerRight: 0.3,
  },
  sadness: {
    browInnerUp: 0.6,
    browDownLeft: 0.2,
    browDownRight: 0.2,
    eyeSquintLeft: 0.2,
    eyeSquintRight: 0.2,
  },
};

const _PROTECTED_SHAPE_RX = /(mouth|lip|jaw|cheek)/i;

export class A2FEmotionRenderer {
  /**
   * @param {object} opts
   * @param {object} opts.blendShapeControls - { name: {mesh, index, value} | [...] }
   * @param {object} opts.driver - A2FLipSyncDriver instance (provides
   *                               onEmotions + onAfterTick subscription).
   * @param {number} [opts.decayHoldMs=500]      Hold target full strength for
   *                                             this long after the last update
   *                                             before fading to zero.
   * @param {number} [opts.decayDurationMs=1500] Linear fade time after the hold.
   * @param {number} [opts.smoothingTauMs=120]   Time constant for easing
   *                                             current values toward target.
   * @param {number} [opts.modelMax=1.0]         Per-rig morph cap. Most rigs use 1.0;
   *                                             rigs that drive 0..100 set this to 100.
   * @param {boolean} [opts.debug=true]
   * @param {object|null} [opts.mapping]         Override A2F_TO_ARKIT_MAP at construction.
   */
  constructor(opts = {}) {
    this.blendShapeControls = opts.blendShapeControls || null;
    this._driver = opts.driver || null;
    this._decayHoldMs = opts.decayHoldMs ?? 500;
    this._decayDurationMs = opts.decayDurationMs ?? 1500;
    this._smoothingTauMs = opts.smoothingTauMs ?? 120;
    this._modelMax = opts.modelMax ?? 1.0;
    this._debug = opts.debug !== false;
    this._mapping = opts.mapping || A2F_TO_ARKIT_MAP;

    // Live state.
    this._currentWeights = {}; // ARKit-name -> 0..1
    this._targetWeights = {}; // ARKit-name -> 0..1
    this._lastEmotionUpdateMs = 0; // 0 = no emotion ever received
    this._lastEmotionVector = null;
    this._lastTickMs = 0;

    // Manual override (debug panel can pin a vector). When non-null we
    // ignore A2F-driven updates entirely and treat this as the source.
    this._manualEmotionVector = null;

    // Cached resolution: ARKit-name -> control(s) or null.
    this._resolveCache = {};
    this._suffixIndex = null; // Lazy-built lowercase-suffix → keys[].

    // Per-shape "ever touched" set so we can keep applying 0 during decay
    // even after the emotion stops contributing — otherwise a once-active
    // shape would freeze at its last value.
    this._everTouched = new Set();

    // Driver subscriptions; kept so we can unsubscribe on dispose.
    this._unsubEmotions = null;
    this._unsubAfterTick = null;

    // Independent rAF for silence-decay (only does work when driver idle).
    this._idleRafId = null;
    this._boundIdleTick = this._idleTick.bind(this);

    // External-tick handoff. When a host render loop drives this renderer
    // via `renderTick(now)` (ModelViewer.animate does, right before the
    // single `renderer.render()` call), the renderer's own self-scheduled
    // paths (the silence-decay `_idleTick` rAF AND the driver's per-frame
    // `onAfterTick` overlay) stand down. This collapses every facial write
    // to ONE cadence and makes the emotion overlay the deterministic FINAL
    // write before each rendered frame, so it no longer races the blink /
    // eye-tracking controllers (which also run inside the render loop) or
    // the lip-sync driver's frame/decay loops on shared eye/brow shapes —
    // the flicker the desktop 60 FPS frame-cap exposed. If external ticks
    // stop arriving (no host loop, e.g. headless tests) the self loop
    // automatically resumes after `_externalTickGraceMs`.
    this._externallyDriven = false;
    this._lastExternalTickMs = 0;
    this._externalTickGraceMs = 250;

    this._disposed = false;

    if (this._driver) {
      this._unsubEmotions = this._driver.onEmotions((vec, _tc) =>
        this._onEmotions(vec),
      );
      if (typeof this._driver.onAfterTick === "function") {
        this._unsubAfterTick = this._driver.onAfterTick((now) => {
          // Stand down while a host render loop is driving us — it ticks
          // the emotion overlay AFTER lip-sync each rendered frame, so a
          // second per-frame overlay here would just reintroduce a rival
          // writer cadence.
          if (this._isExternallyDriven(now)) return;
          this._tick(now);
        });
      }
      // Seed from any vector the driver already cached.
      if (this._driver.lastEmotions) {
        this._onEmotions(this._driver.lastEmotions);
      }
    }

    this._idleRafId = requestAnimationFrame(this._boundIdleTick);

    if (this._debug) {
      const count = this.blendShapeControls
        ? Object.keys(this.blendShapeControls).length
        : 0;
      console.log(
        `[A2FEmotionRenderer] Initialized (decayHold=${this._decayHoldMs}ms, decayDur=${this._decayDurationMs}ms, smoothTau=${this._smoothingTauMs}ms, blendShapes=${count})`,
      );
    }
  }

  /**
   * Replace the model's blendShapeControls (e.g. when the user switches
   * characters). Also flushes the resolve cache so suffix lookups are redone.
   */
  setBlendShapeControls(controls) {
    this.blendShapeControls = controls || null;
    this._resolveCache = {};
    this._suffixIndex = null;
    this._currentWeights = {};
    this._everTouched = new Set();
    if (this._debug) {
      const count = controls ? Object.keys(controls).length : 0;
      console.log(
        `[A2FEmotionRenderer] setBlendShapeControls (count=${count})`,
      );
    }
  }

  /**
   * Force the renderer to use this exact emotion vector and ignore A2F
   * updates until `clearManualEmotionVector()` is called. Used by the
   * debug panel.
   */
  setManualEmotionVector(vec) {
    this._manualEmotionVector = vec ? { ...vec } : null;
    if (this._manualEmotionVector) {
      this._onEmotions(this._manualEmotionVector);
    } else {
      // Clear the target so we decay back to neutral immediately if no
      // more A2F input comes.
      this._targetWeights = {};
      this._lastEmotionUpdateMs = performance.now();
    }
  }

  clearManualEmotionVector() {
    this.setManualEmotionVector(null);
  }

  isManualOverrideActive() {
    return !!this._manualEmotionVector;
  }

  /** Snapshot of last A2F vector we computed targets from. */
  getLastEmotionVector() {
    return this._lastEmotionVector ? { ...this._lastEmotionVector } : null;
  }

  /** Tear down — stops rAF, unsubscribes from driver. Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    if (this._unsubEmotions) {
      try {
        this._unsubEmotions();
      } catch (_) {}
      this._unsubEmotions = null;
    }
    if (this._unsubAfterTick) {
      try {
        this._unsubAfterTick();
      } catch (_) {}
      this._unsubAfterTick = null;
    }
    if (this._idleRafId) {
      cancelAnimationFrame(this._idleRafId);
      this._idleRafId = null;
    }
  }

  // -------------------- internals --------------------

  _onEmotions(vec) {
    if (this._disposed) return;
    if (!vec || typeof vec !== "object") return;
    // If a manual override is pinned and this is an A2F-driven update,
    // ignore the A2F input.
    if (this._manualEmotionVector && vec !== this._manualEmotionVector) {
      return;
    }

    this._lastEmotionVector = vec;
    const targets = {};
    for (let i = 0; i < A2F_EMOTION_NAMES.length; i++) {
      const name = A2F_EMOTION_NAMES[i];
      const strength = Number(vec[name]);
      if (!strength || strength <= 0.001) continue;
      const map = this._mapping[name];
      if (!map) continue;
      const keys = Object.keys(map);
      for (let j = 0; j < keys.length; j++) {
        const shape = keys[j];
        if (_PROTECTED_SHAPE_RX.test(shape)) continue;
        const contribution = map[shape] * strength;
        targets[shape] = (targets[shape] || 0) + contribution;
      }
    }
    // Cap each shape at 1.0 so a multi-emotion stack never over-saturates.
    const keys = Object.keys(targets);
    for (let i = 0; i < keys.length; i++) {
      if (targets[keys[i]] > 1.0) targets[keys[i]] = 1.0;
    }

    this._targetWeights = targets;
    this._lastEmotionUpdateMs = performance.now();

    // Mark every contributing shape as "ever touched" so decay continues
    // to write zero into shapes that drop out of the target later.
    for (let i = 0; i < keys.length; i++) {
      this._everTouched.add(keys[i]);
    }
  }

  /**
   * True while a host render loop has called `renderTick(now)` within the
   * last `_externalTickGraceMs`. While true, the self-scheduled paths
   * (`_idleTick`, the driver `onAfterTick` overlay) stand down so there is
   * exactly one emotion-overlay write per rendered frame.
   */
  _isExternallyDriven(now) {
    if (!this._externallyDriven) return false;
    const t =
      typeof now === "number"
        ? now
        : typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
    return t - this._lastExternalTickMs < this._externalTickGraceMs;
  }

  /**
   * Drive one emotion overlay tick from a host render loop. ModelViewer's
   * single render loop calls this AFTER the blink / eye-tracking
   * controllers and immediately BEFORE `renderer.render()`, so the emotion
   * eye/brow/nose shapes are always the last write before the frame is
   * presented. Running on the render cadence (and as the final writer)
   * removes the cross-rAF flicker the desktop frame-cap exposed.
   *
   * @param {number} [now] performance.now() of the current render frame.
   */
  renderTick(now) {
    if (this._disposed) return;
    const t =
      typeof now === "number"
        ? now
        : typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
    this._externallyDriven = true;
    this._lastExternalTickMs = t;
    this._tick(t);
  }

  _idleTick(now) {
    if (this._disposed) return;
    this._idleRafId = requestAnimationFrame(this._boundIdleTick);
    // A host render loop owns the cadence right now — it ticks us as the
    // final facial write each rendered frame. Skip the self loop so we
    // don't double-apply (or apply off the render cadence).
    if (this._isExternallyDriven(now)) return;
    // If the driver is currently driving frames, its onAfterTick will
    // call _tick — skip ours to avoid double-applying.
    if (this._driver && this._driver._playing) return;
    this._tick(now);
  }

  _tick(now) {
    if (this._disposed) return;
    if (!this.blendShapeControls) return;

    // A2F IS KING. While the lip-sync driver is actively producing per-frame
    // blendshapes (i.e. the character is SPEAKING), those frames already carry
    // the correct, audio-derived expression for the ENTIRE face — upper AND
    // lower — straight from NVIDIA Audio2Face. This local emotion overlay must
    // write NOTHING during speech: overlaying our coarse 10-emotion mapping on
    // top would OVERWRITE A2F's genuine per-frame brows / eyes / nose. So while
    // any A2F driver is active we stand down completely and drop our running
    // state, then ease back in from rest once A2F goes idle. This overlay is an
    // IDLE-ONLY layer; during speech A2F is the sole writer of the whole face.
    const driverCls = this._driver && this._driver.constructor;
    if (
      driverCls &&
      typeof driverCls.isAnyActive === "function" &&
      driverCls.isAnyActive()
    ) {
      if (this._everTouched.size || Object.keys(this._currentWeights).length) {
        this._currentWeights = {};
        this._everTouched.clear();
        this._lastTickMs = 0;
      }
      return;
    }

    const dt = this._lastTickMs
      ? Math.max(1, Math.min(100, now - this._lastTickMs))
      : 16;
    this._lastTickMs = now;

    // Compute decay multiplier for the target vector.
    let decayMul = 1.0;
    if (this._lastEmotionUpdateMs > 0) {
      const sinceUpdate = now - this._lastEmotionUpdateMs;
      if (sinceUpdate <= this._decayHoldMs) {
        decayMul = 1.0;
      } else if (sinceUpdate >= this._decayHoldMs + this._decayDurationMs) {
        decayMul = 0;
      } else {
        decayMul =
          1.0 -
          (sinceUpdate - this._decayHoldMs) / this._decayDurationMs;
      }
    } else {
      // No emotion ever received → effective target is 0 everywhere.
      decayMul = 0;
    }

    // Time-constant smoothing: alpha = 1 - exp(-dt/tau).
    const alpha = 1 - Math.exp(-dt / this._smoothingTauMs);

    // Iterate over union of (target shapes ∪ ever-touched shapes).
    // Ever-touched ensures decaying shapes keep being written down to 0.
    const union = new Set(Object.keys(this._targetWeights));
    for (const k of this._everTouched) union.add(k);

    let shapesActuallyApplied = 0;
    for (const shape of union) {
      const target = (this._targetWeights[shape] || 0) * decayMul;
      const current = this._currentWeights[shape] || 0;
      const next = current + (target - current) * alpha;
      this._currentWeights[shape] = next;
      if (this._applyShape(shape, next * this._modelMax)) {
        shapesActuallyApplied++;
      }

      // Once a shape has settled near zero AND target is zero, drop it
      // from the ever-touched set so we stop wasting work on it.
      if (target < 1e-4 && Math.abs(next) < 1e-3) {
        this._currentWeights[shape] = 0;
        this._applyShape(shape, 0);
        this._everTouched.delete(shape);
      }
    }

    // (debug spam guard — only log once per ~5 s)
    if (this._debug && now - (this._lastDebugLogMs || 0) > 5000) {
      this._lastDebugLogMs = now;
      const active = Object.entries(this._currentWeights)
        .filter(([_, v]) => v > 0.05)
        .map(([k, v]) => `${k}=${v.toFixed(2)}`);
      if (active.length > 0) {
        console.log(
          `[A2FEmotionRenderer] tick: applied=${shapesActuallyApplied} active=[${active.join(", ")}] decayMul=${decayMul.toFixed(2)}`,
        );
      }
    }
  }

  _buildSuffixIndex() {
    // Lowercase-suffix-after-last-dot → first matching control key.
    // For ARKit shapes named like `blendShape1.browInnerUp`, this lets us
    // resolve `browInnerUp` straight to `blendShape1.browInnerUp`.
    this._suffixIndex = new Map();
    if (!this.blendShapeControls) return;
    const keys = Object.keys(this.blendShapeControls);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const lk = key.toLowerCase();
      if (!this._suffixIndex.has(lk)) this._suffixIndex.set(lk, key);
      const dot = key.lastIndexOf(".");
      if (dot !== -1) {
        const suf = key.substring(dot + 1).toLowerCase();
        if (!this._suffixIndex.has(suf)) this._suffixIndex.set(suf, key);
      }
    }
  }

  _resolve(arkitName) {
    if (this._resolveCache[arkitName] !== undefined) {
      return this._resolveCache[arkitName];
    }
    let resolved = null;
    if (this.blendShapeControls) {
      if (this.blendShapeControls[arkitName]) {
        resolved = arkitName;
      } else {
        if (!this._suffixIndex) this._buildSuffixIndex();
        const lookup = this._suffixIndex.get(arkitName.toLowerCase());
        if (lookup && this.blendShapeControls[lookup]) {
          resolved = lookup;
        }
      }
    }
    this._resolveCache[arkitName] = resolved;
    return resolved;
  }

  _applyShape(arkitName, value) {
    const key = this._resolve(arkitName);
    if (!key) return false;
    const ctl = this.blendShapeControls[key];
    if (!ctl) return false;
    if (Array.isArray(ctl)) {
      for (let i = 0; i < ctl.length; i++) this._setMorph(ctl[i], value);
    } else {
      this._setMorph(ctl, value);
    }
    return true;
  }

  _setMorph(ctl, value) {
    if (!ctl || !ctl.mesh || typeof ctl.index !== "number") return;
    const arr = ctl.mesh.morphTargetInfluences;
    if (!arr || ctl.index < 0 || ctl.index >= arr.length) return;
    arr[ctl.index] = value;
    ctl.value = value;
  }
}

// Re-exported primarily so tests / debug UIs don't have to import the
// matrix from this module's source.
export const A2F_PROTECTED_SHAPE_RX = _PROTECTED_SHAPE_RX;
